/**
 * Validation Patterns
 * 
 * Centralized regex patterns for form validation across the application.
 * These patterns are used for client-side and server-side validation.
 * 
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types
 */

/**
 * Email pattern: supports modern TLDs (up to 63 chars) and international domains
 * Format: user@domain.tld
 */
export const EMAIL_PATTERN: string = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,63}$';

/**
 * Text pattern: allows names starting with letters, supports international characters
 * Allows optional leading/trailing spaces but requires at least one letter
 * Supports: letters, spaces, periods, commas, apostrophes, hyphens
 */
export const TEXT_PATTERN: string = "^[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ .,'-]*$";

/**
 * Mixed pattern: general pattern for non-USA postal codes (alphanumeric)
 * For USA, use USA_ZIP_PATTERN instead
 * Supports international characters and common postal code formats
 */
export const MIXED_PATTERN: string = '^[a-zA-Z0-9][a-zA-Z0-9 .,\'-¡«¿ßäéíñöüÄÉÍÑÖÜàÀÈèÌìòÒùÙáéíóúýÁÉÍÓÚÝ]*$';

/**
 * USA ZIP code pattern: 5 digits, optional dash, 4 digits
 * Examples: 91320 or 91320-1234
 */
export const USA_ZIP_PATTERN: string = '^\\d{5}(-\\d{4})?$';

/**
 * Canadian postal code pattern: A1A 1A1 format
 * Format: Letter-Digit-Letter [space] Digit-Letter-Digit
 * Example: K1A 0B1
 */
export const CANADIAN_POSTAL_PATTERN: string = '^[A-Za-z]\\d[A-Za-z]\\s?\\d[A-Za-z]\\d$';

/**
 * Number pattern: only digits
 * Used for numeric-only fields
 */
export const NUMBER_PATTERN: string = '^[\\d]*$';

/**
 * Phone pattern: requires at least one digit, allows international formats
 * Supports formats like: +1 (555) 123-4567, 555-1234, (555) 123-4567 ext. 123, etc.
 * TOUC-1036
 */
export const PHONE_PATTERN: string = '^[\\s\\+\\-\\(\\)\\.xX#\\/]*[\\d]+[\\d\\s\\+\\-\\(\\)\\.xX#\\/]*$';

/**
 * Address pattern: supports international addresses with special characters
 * Escaped special regex characters properly, allows common address characters
 * TOUC-1036, DEV-35015 adds german and spanish characters
 */
export const ADDRESS_PATTERN: string = '^[A-Za-z0-9\\s\\.,\\-\\#\\+\\%\\(\\)\\:\\;\\=\\?\\[\\]\\/¡«¿ßäéíñöüÄÉÍÑÖÜàÀÈèÌìòÒùÙáéíóúýÁÉÍÓÚÝ]+$';

/**
 * Name on card pattern: for payment card name fields
 * TOUC-963 to match 1SC control better, and make it xml safe to support TCR api
 */
export const NAME_ON_CARD_PATTERN: string = '[,.&[\/A-Za-z0-9 -]*';

/**
 * Country names constants for postal code validation
 */
export const COUNTRIES = {
  UNITED_STATES: 'United States',
  CANADA: 'Canada'
} as const;

